import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Eye, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import Seo from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
const STAGES = ["submitted", "interview", "board", "approved", "declined"];

function StatusPill({ status, t }) {
  const cls = {
    approved: "border-white bg-white text-black",
    declined: "border-white/30 text-neutral-400",
    submitted: "border-white/40 text-white",
    interview: "border-white/60 text-white",
    board: "border-white/80 text-white",
    draft: "border-white/20 text-neutral-500",
  }[status] || "border-white/20 text-neutral-500";
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full border text-[10px] tracking-[0.25em] uppercase ${cls}`}>
      {t(`vc.onb.status.${status}`)}
    </span>
  );
}

export default function AdminApplicationsPage() {
  const navigate = useNavigate();
  const { user, loading, initialized } = useAuth();
  const { t } = useTranslation();
  const [apps, setApps] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(null);
  const [nextStatus, setNextStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!initialized || loading) return;
    if (!user) { navigate("/login"); return; }
    if (!user.is_admin) { navigate("/"); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/applications`, { withCredentials: true });
        setApps(data.applications || []);
      } catch (e) {
        toast.error("Failed to load applications");
      } finally { setLoadingList(false); }
    })();
  }, [initialized, loading, user, navigate]);

  const openDetail = (a) => {
    setSelected(a);
    setNextStatus(a.status);
    setNote("");
  };

  const updateStatus = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/api/admin/applications/${selected.app_id}`, {
        status: nextStatus,
        note,
      }, { withCredentials: true });
      const { data } = await axios.get(`${API}/api/admin/applications`, { withCredentials: true });
      setApps(data.applications || []);
      const refreshed = (data.applications || []).find((x) => x.app_id === selected.app_id);
      setSelected(refreshed);
      setNote("");
      toast.success(t("vc.adm.apps.updating"));
    } catch (e) {
      toast.error("Update failed");
    } finally { setSaving(false); }
  };

  if (!initialized || loading || loadingList) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white marble-bg" data-testid="admin-apps-page">
      <Seo title={t("vc.adm.apps.title")} noindex />
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#050505]/80 border-b border-white/[0.06]">
        <div className="max-w-[1300px] mx-auto px-5 h-[54px] flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 hover:text-white transition-colors">
            <ArrowLeft size={14} /> {t("vc.common.back")}
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo-light.png" alt="" className="h-5 w-auto" aria-hidden="true" />
            <div className="font-serif text-[15px]">Vangard<span className="text-neutral-500">Club</span></div>
          </div>
          <LangSwitcher />
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-5 pt-24 pb-16">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[9px] tracking-[0.35em] uppercase text-neutral-500">Admin</div>
            <h1 className="font-serif text-2xl mt-1.5 leading-tight">{t("vc.adm.apps.title")}</h1>
          </div>
          <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">{apps.length}</span>
        </div>
        <p className="mt-2 text-sm text-neutral-500 font-light max-w-xl">{t("vc.adm.apps.sub")}</p>

        <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 overflow-hidden">
          {apps.length === 0 ? (
            <div className="p-10 text-center text-neutral-500 text-sm">{t("vc.adm.apps.empty")}</div>
          ) : (
            <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-sm min-w-[540px]">
              <thead>
                <tr className="text-[9px] tracking-[0.25em] uppercase text-neutral-500 border-b border-white/[0.06]">
                  <th className="text-left py-2.5 px-4 font-normal">{t("vc.adm.apps.name")}</th>
                  <th className="text-left py-2.5 px-4 font-normal">{t("vc.adm.apps.stage")}</th>
                  <th className="text-left py-2.5 px-4 font-normal hidden md:table-cell">{t("vc.adm.apps.updated")}</th>
                  <th className="text-right py-2.5 px-4 font-normal">{t("vc.adm.apps.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.app_id || a.user_id || a.email} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-testid={`admin-app-row-${a.app_id}`}>
                    <td className="py-2.5 px-4">
                      <div className="text-sm">{a.full_name || "—"}</div>
                      <div className="text-[11px] text-neutral-500">{a.email}</div>
                    </td>
                    <td className="py-2.5 px-4"><StatusPill status={a.status} t={t} /></td>
                    <td className="py-2.5 px-4 text-[11px] text-neutral-500 hidden md:table-cell">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}</td>
                    <td className="py-2.5 px-4 text-right">
                      <button onClick={() => openDetail(a)} className="inline-flex items-center gap-1.5 border border-white/20 text-white px-3 py-1.5 rounded-full text-[9px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/55 transition-colors" data-testid={`admin-app-open-${a.app_id}`}>
                        <Eye size={11} /> {t("vc.adm.apps.view")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </main>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start justify-center pt-10 pb-10 px-4 overflow-auto"
            onClick={(e) => e.target === e.currentTarget && setSelected(null)}
            data-testid="admin-app-detail"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="w-full max-w-2xl bg-[#0A0A0A] border border-white/[0.10] rounded-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06]">
                <div className="flex items-center gap-3">
                  {selected.photo && <img src={selected.photo} alt="" className="w-9 h-9 rounded-full object-cover grayscale border border-white/20" />}
                  <div>
                    <div className="text-sm">{selected.full_name}</div>
                    <div className="text-[11px] text-neutral-500">{selected.email}</div>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="p-1.5 rounded-full hover:bg-white/5" data-testid="admin-detail-close"><X size={16} /></button>
              </div>

              <div className="p-5 space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3.5 text-sm">
                  <div><div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500">{t("vc.onb.age")}</div><div className="mt-0.5">{selected.age}</div></div>
                  <div><div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500">{t("vc.onb.turnover")}</div><div className="mt-0.5">{selected.turnover || "—"}</div></div>
                  <div><div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500">{t("vc.onb.years")}</div><div className="mt-0.5">{selected.years_in_business ?? "—"}</div></div>
                  <div><div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500">{t("vc.onb.phone")}</div><div className="mt-0.5">{selected.phone || "—"}</div></div>
                  <div><div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500">{t("vc.onb.telegram")}</div><div className="mt-0.5">{selected.telegram_id || "—"}</div></div>
                  <div><div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500">Instagram · LinkedIn</div><div className="mt-0.5 text-[11px] break-all">{selected.instagram || "—"} · {selected.linkedin || "—"}</div></div>
                </div>

                <div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500 mb-1.5">{t("vc.adm.apps.business")}</div>
                  <p className="text-[13px] text-neutral-300 font-light leading-relaxed">{selected.business_desc}</p>
                </div>

                <div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500 mb-1.5">{t("vc.adm.apps.motivation")}</div>
                  <p className="text-[13px] text-neutral-300 font-light leading-relaxed whitespace-pre-wrap">{selected.why_join}</p>
                </div>

                <div className="border-t border-white/[0.06] pt-4">
                  <div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500 mb-2.5">{t("vc.adm.apps.set-stage")}</div>
                  <div className="flex flex-wrap gap-2">
                    {STAGES.map((s) => (
                      <button
                        key={s}
                        onClick={() => setNextStatus(s)}
                        className={`text-[10px] px-3 py-1.5 rounded-full border transition-colors ${nextStatus === s ? "bg-white text-black border-white" : "border-white/20 text-neutral-300 hover:border-white/55"}`}
                        data-testid={`admin-stage-${s}`}
                      >
                        {t(`vc.onb.status.${s}`)}
                      </button>
                    ))}
                  </div>
                  <textarea
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t("vc.adm.apps.note")}
                    className="mt-3 w-full bg-transparent border border-white/[0.10] rounded-xl focus:border-white/45 outline-none py-2.5 px-3.5 text-[13px] font-light transition-colors placeholder:text-neutral-600"
                    data-testid="admin-detail-note"
                  />
                  <div className="mt-3 flex items-center justify-end">
                    <button
                      onClick={updateStatus}
                      disabled={saving || nextStatus === selected.status}
                      className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 disabled:opacity-40 transition-colors"
                      data-testid="admin-detail-save"
                    >
                      {saving ? t("vc.adm.apps.updating") : t("vc.adm.apps.confirm")}
                    </button>
                  </div>
                </div>

                {selected.history && selected.history.length > 0 && (
                  <div className="border-t border-white/[0.06] pt-4">
                    <div className="text-[9px] tracking-[0.25em] uppercase text-neutral-500 mb-2">{t("vc.adm.apps.history")}</div>
                    <ul className="space-y-1.5 text-[11px] text-neutral-400 font-light">
                      {selected.history.slice().reverse().map((h, i) => (
                        <li key={i}>
                          <span className="text-neutral-600 tabular-nums">{new Date(h.at).toLocaleString()}</span>{" "}
                          · <span className="text-white/90">{t(`vc.onb.status.${h.status}`)}</span>
                          {h.note && <span className="text-neutral-500"> — {h.note}</span>}
                          {h.by && <span className="text-neutral-600"> · {h.by}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
